"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Typography,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatUnderlined,
} from "@mui/icons-material";

// Tiptap Imports
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";

// Props interface is identical to your old TextEditor
interface TextEditorProps {
  data: {
    text: string;
    style?: {
      color?: string;
      fontSize?: string;
      width?: string;
      fontFamily?: string;
      textAlign?: string;
      lineHeight?: string;
      letterSpacing?: string;
      fontWeight?: string;
    };
  };
  onChange: (data: any) => void;
}

const defaultStyle = {
  color: "black",
  fontSize: "16px",
  width: "100%",
  fontFamily: "Arial",
  textAlign: "left" as const,
  lineHeight: "1.5",
  letterSpacing: "normal",
  fontWeight: "normal" as const,
};

/**
 * A simple Tiptap toolbar using Material-UI ToggleButtons
 */
const TiptapToolbar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        borderBottom: "1px solid #ddd",
        display: "flex",
        flexWrap: "wrap",
      }}
    >
      <ToggleButtonGroup size="small" aria-label="text formatting">
        <ToggleButton
          value="bold"
          selected={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlined />
        </ToggleButton>
      </ToggleButtonGroup>
      <ToggleButtonGroup
        size="small"
        aria-label="text alignment"
        sx={{ ml: 1 }}
      >
        <ToggleButton
          value="left"
          selected={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value="center"
          selected={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value="right"
          selected={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          <FormatAlignRight />
        </ToggleButton>
        <ToggleButton
          value="justify"
          selected={editor.isActive({ textAlign: "justify" })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        >
          <FormatAlignJustify />
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );
};

/**
 * A modern text editor using Tiptap, which replaces the old CKEditor component.
 * It maintains the same prop structure (data, onChange) as the old component.
 */
export const TiptapTextEditor: React.FC<TextEditorProps> = ({
  data,
  onChange,
}) => {
  // State for the WRAPPER div's style (from your old component)
  const [style, setStyle] = useState({ ...defaultStyle, ...data.style });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: data.text, // Set initial content
    onUpdate: ({ editor }) => {
      // When Tiptap content changes, call the main onChange prop
      onChange({
        text: editor.getHTML(),
        style: style, // Pass the current wrapper style
      });
    },
  });

  // Update Tiptap's content if the 'data.text' prop changes
  useEffect(() => {
    if (editor && data.text !== editor.getHTML()) {
      editor.commands.setContent(data.text, { emitUpdate: false });
    }
  }, [data.text, editor]);

  // Update wrapper style if 'data.style' prop changes
  useEffect(() => {
    setStyle({ ...defaultStyle, ...data.style });
  }, [data.style]);

  // This function is identical to your old one.
  // It controls the WRAPPER div's style.
  const handleStyleChange = (styleProp: string, value: string) => {
    const updatedStyle = { ...style, [styleProp]: value };
    setStyle(updatedStyle);
    onChange({
      text: editor?.getHTML() || data.text, // Get current HTML from editor
      style: updatedStyle,
    });
  };

  // Render a loading state until the client-side editor is ready
  if (!editor) {
    return <CircularProgress />;
  }

  return (
    <Stack spacing={2}>
      {/* 1. The Tiptap Editor */}
      <Paper variant="outlined">
        <TiptapToolbar editor={editor} />
        <EditorContent
          editor={editor}
          style={{ minHeight: "200px", padding: "16px", outline: "none" }}
        />
      </Paper>

      {/* 2. The Wrapper Style Controls (Identical to your old component) */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Wrapper Style
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Color"
              type="color"
              value={style.color}
              onChange={(e) => handleStyleChange("color", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Font Size (px)"
              type="text" // Use text for units like '16px' or '1.5rem'
              value={style.fontSize}
              onChange={(e) => handleStyleChange("fontSize", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Font Family</InputLabel>
              <Select
                value={style.fontFamily}
                onChange={(e) =>
                  handleStyleChange("fontFamily", e.target.value)
                }
              >
                <MenuItem value="Arial">Arial</MenuItem>
                <MenuItem value="Georgia">Georgia</MenuItem>
                <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                <MenuItem value="Verdana">Verdana</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Text Align</InputLabel>
              <Select
                value={style.textAlign}
                onChange={(e) => handleStyleChange("textAlign", e.target.value)}
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
                <MenuItem value="justify">Justify</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Line Height"
              type="text"
              value={style.lineHeight}
              onChange={(e) => handleStyleChange("lineHeight", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Letter Spacing"
              type="text"
              value={style.letterSpacing}
              onChange={(e) =>
                handleStyleChange("letterSpacing", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Font Weight</InputLabel>
              <Select
                value={style.fontWeight}
                onChange={(e) =>
                  handleStyleChange("fontWeight", e.target.value)
                }
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="bold">Bold</MenuItem>
                <MenuItem value="bolder">Bolder</MenuItem>
                <MenuItem value="lighter">Lighter</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Width"
              type="text"
              value={style.width}
              onChange={(e) => handleStyleChange("width", e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
